import { useEffect, useMemo, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClipboardCheck, Database } from 'lucide-react';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';

const TAB_ROUTES = [
  { path: 'review', labelKey: 'forecastModule.tabs.review', icon: ClipboardCheck },
  {
    path: 'data-validation',
    labelKey: 'forecastModule.tabs.dataValidation',
    icon: Database,
    action: 'forecast.review_data_validation' as const,
  },
] as const;

function tabClass(isActive: boolean): string {
  return [
    'inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
    isActive
      ? 'border-stocker-primary text-stocker-primary'
      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900',
  ].join(' ');
}

/** Tabs fijos; el scroll vive solo en el panel del tab activo. */
export function ForecastModuleLayout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { isActionEnabled } = useFeatureVisibility();
  const panelRef = useRef<HTMLDivElement>(null);
  const basePath = '/forecast';

  const visibleTabs = useMemo(
    () =>
      TAB_ROUTES.filter((tab) => {
        if (!('action' in tab) || !tab.action) return true;
        return isActionEnabled(tab.action, false);
      }),
    [isActionEnabled]
  );

  useEffect(() => {
    panelRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 shadow-sm md:px-6">
        <nav className="-mb-px flex flex-wrap gap-1" aria-label={t('forecastModule.tabsAria')}>
          {visibleTabs.map(({ path, labelKey, icon: Icon }) => (
            <NavLink
              key={path}
              to={`${basePath}/${path}`}
              className={({ isActive }) => tabClass(isActive)}
              end={path === 'review'}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {t(labelKey)}
            </NavLink>
          ))}
        </nav>
      </div>
      <div ref={panelRef} className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
